import { Command } from "commander";

/**
 * Creates the `skaile serve` command.
 *
 * Starts a WebSocket agent server for platform integration, forwarding
 * incoming protocol messages to the configured driver. Supports
 * `--compile-test` to verify the binary build before a Docker deployment.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/serve#make-serve-command
 */
export function makeServeCommand(): Command {
  return new Command("serve")
    .description("Start a WebSocket agent server (for platform integration)")
    .option("--port <port>", "WebSocket server port", "8080")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--driver <name>", "Override driver")
    .option("--model <name>", "Override model")
    .option("--provider <name>", "Override provider")
    .option("--session-id <id>", "Session ID for conversation continuity")
    .option("--resume-session-id <id>", "OMP session ID to resume (restores native OMP context)")
    .option("--compile-test", "Build a compiled binary and run it (simulates Docker environment)")
    .option(
      "--with-codex",
      "Include the Codex backend in the compiled binary (used with --compile-test)",
    )
    .action(
      async (opts: {
        port: string;
        projectDir: string;
        driver?: string;
        model?: string;
        provider?: string;
        sessionId?: string;
        resumeSessionId?: string;
        compileTest?: boolean;
        withCodex?: boolean;
      }) => {
        if (opts.compileTest) {
          await runCompileTest({ ...opts, withCodex: opts.withCodex ?? false });
          return;
        }

        // Best-effort plugin store: reconcile + load any `plugins:` declared in
        // skaile.yaml before the runner starts. Guarded no-op (zero overhead)
        // when the project declares no plugins — see cli/CLAUDE.md § Plugin store.
        await ensurePluginsLoadedForServe(opts.projectDir);

        const { startAgentServer } = await import("@skaile/workspaces/runner");

        await startAgentServer({
          port: parseInt(opts.port, 10),
          projectDir: opts.projectDir,
          driver: opts.driver,
          model: opts.model,
          provider: opts.provider,
          sessionId: opts.sessionId,
          resumeSessionId: opts.resumeSessionId,
        });
      },
    );
}

/**
 * Reconcile + load `skaile.yaml` `plugins:` into the process-wide
 * `pluginRegistry` before the runner starts. Best-effort: a plugin that fails
 * to reconcile or load is logged and skipped — it never blocks serve startup.
 *
 * Fast path: when the project declares no `plugins:`, `ensurePluginsLoaded`
 * returns immediately, so the common case costs a single config read.
 */
async function ensurePluginsLoadedForServe(projectDir: string): Promise<void> {
  const { logInfo, logWarn } = await import("../helpers.ts");
  try {
    const { resolveSkWorkspaceConfig } = await import("@skaile/workspaces/core");
    const config = resolveSkWorkspaceConfig(projectDir);
    if (!config.plugins || config.plugins.length === 0) return;

    const { ensurePluginsLoaded } = await import("../plugin-store/index.ts");
    const { pluginRegistry } = await import("@skaile/workspaces/plugin-registry");
    const result = await ensurePluginsLoaded(projectDir, config.plugins, pluginRegistry);

    if (result.loaded.length > 0) {
      logInfo(`Loaded ${result.loaded.length} plugin(s): ${result.loaded.join(", ")}`);
    }
    for (const f of result.failed) {
      logWarn(f.error);
    }
  } catch (err) {
    // Never fail serve startup on a plugin store error — log and continue.
    logWarn(`plugin store skipped: ${err instanceof Error ? err.message : String(err)}`);
  }
}

async function runCompileTest(opts: {
  port: string;
  projectDir: string;
  driver?: string;
  model?: string;
  provider?: string;
  sessionId?: string;
  withCodex?: boolean;
}): Promise<void> {
  const { logOk, logErr, logInfo, S } = await import("../helpers.ts");
  const { tmpdir } = await import("node:os");
  const { dirname, join, resolve } = await import("node:path");
  const { fileURLToPath } = await import("node:url");
  const { unlinkSync, existsSync } = await import("node:fs");
  const { spawn } = await import("node:child_process");
  const { portableSpawnSync } = await import("@skaile/workspaces/core");
  const { MONOREPO_ROOT } = await import("../paths.ts");

  // `--compile-test` runs `bun build --compile` against the live CLI source
  // to verify a Docker-style static binary still links. That source tree only
  // exists inside the skaile-dev workspace; once @skaile/workspace-cli has
  // been npm-installed, the compiled `dist/` is all that ships, so the test
  // is meaningless and the entry path does not resolve. Gate accordingly.
  if (MONOREPO_ROOT === null) {
    logErr("--compile-test is only available inside the skaile-dev workspace.");
    logInfo("Run it from a checkout of skaile-ai/skaile-dev (the monorepo shell).");
    process.exitCode = 2;
    return;
  }

  const outfile = join(tmpdir(), `skaile-compile-test-${Date.now()}`);
  // Anchor on this file's own location instead of MONOREPO_ROOT — the prior
  // `join(MONOREPO_ROOT, "cli", "src", "index.ts")` join was incorrect (the
  // CLI lives under `workspaces/cli/`, not directly under the shell repo
  // root). Resolving via import.meta.url is correct regardless of monorepo
  // depth or whether we are running from source vs dist.
  const __thisFile = fileURLToPath(import.meta.url);
  const entryPoint = resolve(dirname(__thisFile), "..", "index.ts");

  // Step 1: Build
  logInfo(`Building compiled binary...`);
  logInfo(`Entry: ${S.dim(entryPoint)}`);
  logInfo(`Output: ${S.dim(outfile)}`);

  const buildArgs = [
    "bun",
    "build",
    entryPoint,
    "--compile",
    "--outfile",
    outfile,
    "--external",
    "cpu-features",
    "--external",
    "better-sqlite3",
    "--external",
    "pg-native",
    "--define:__INCLUDE_CLAUDE_SDK__=true",
    opts.withCodex ? "--define:__INCLUDE_CODEX__=true" : "--define:__INCLUDE_CODEX__=false",
  ];

  const buildProc = portableSpawnSync(buildArgs, {
    cwd: MONOREPO_ROOT,
    env: { ...process.env },
  });

  if (buildProc.exitCode !== 0) {
    logErr("Build failed:");
    if (buildProc.stderr) console.error(buildProc.stderr);
    if (buildProc.stdout) console.log(buildProc.stdout);
    process.exitCode = 1;
    return;
  }

  logOk(`Binary built (${S.dim(outfile)})`);

  // Step 2: Run the compiled binary with serve args
  const serveArgs = [outfile, "serve", "--port", opts.port, "--project-dir", opts.projectDir];
  if (opts.driver) serveArgs.push("--driver", opts.driver);
  if (opts.model) serveArgs.push("--model", opts.model);
  if (opts.provider) serveArgs.push("--provider", opts.provider);
  if (opts.sessionId) serveArgs.push("--session-id", opts.sessionId);

  logInfo(`Running: ${S.dim(serveArgs.join(" "))}`);
  console.log();

  const [serveBin, ...serveRest] = serveArgs;
  const serveProc = spawn(serveBin as string, serveRest, {
    stdio: "inherit",
    env: { ...process.env },
  });

  // Cleanup on exit
  function cleanup() {
    try {
      serveProc.kill();
    } catch {}
    try {
      if (existsSync(outfile)) unlinkSync(outfile);
    } catch {}
  }

  process.on("SIGINT", () => {
    cleanup();
    process.exit(0);
  });
  process.on("SIGTERM", () => {
    cleanup();
    process.exit(0);
  });

  await new Promise<void>((res) => {
    serveProc.on("close", () => res());
  });
  cleanup();
}
